import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Bike, CarTaxiFront, FilePen, FileSearch, Monitor, UserPlus, Users, ArrowDown } from 'lucide-react';
import { PhoneFrame } from '../components/mobile/PhoneFrame';
import { OwnerSplash } from '../components/owner/screens/OwnerSplash';
import { OwnerOnboarding } from '../components/owner/screens/OwnerOnboarding';
import { OwnerRegister } from '../components/owner/screens/OwnerRegister';
import { OwnerOtp } from '../components/owner/screens/OwnerOtp';
import { OwnerLogin } from '../components/owner/screens/OwnerLogin';
import { OwnerProfileForm } from '../components/owner/screens/OwnerProfileForm';
import { OwnerHome } from '../components/owner/screens/OwnerHome';
import { OwnerAddVehicle } from '../components/owner/screens/OwnerAddVehicle';
import { OwnerVehicleForm } from '../components/owner/screens/OwnerVehicleForm';
import { OwnerDocuments } from '../components/owner/screens/OwnerDocuments';
import { OwnerBrtaCheck } from '../components/owner/screens/OwnerBrtaCheck';
import { OwnerSelfVerify } from '../components/owner/screens/OwnerSelfVerify';
import { OwnerSubmitted } from '../components/owner/screens/OwnerSubmitted';
import { OwnerTracking } from '../components/owner/screens/OwnerTracking';
import { OwnerVehicles } from '../components/owner/screens/OwnerVehicles';
import { OwnerVehicleDetail } from '../components/owner/screens/OwnerVehicleDetail';
import { OwnerDrivers } from '../components/owner/screens/OwnerDrivers';
import { OwnerAddDriver } from '../components/owner/screens/OwnerAddDriver';
import { OwnerDocTracker } from '../components/owner/screens/OwnerDocTracker';
import { OwnerQrSticker } from '../components/owner/screens/OwnerQrSticker';
import { OwnerNotifications } from '../components/owner/screens/OwnerNotifications';
import { OwnerExpiry } from '../components/owner/screens/OwnerExpiry';
import { OwnerCorrection } from '../components/owner/screens/OwnerCorrection';
import { OwnerOwnership } from '../components/owner/screens/OwnerOwnership';
import { OwnerHistory } from '../components/owner/screens/OwnerHistory';
import { OwnerNotices } from '../components/owner/screens/OwnerNotices';
import { OwnerSupport } from '../components/owner/screens/OwnerSupport';
import { OwnerAccount } from '../components/owner/screens/OwnerAccount';
import { useOwnerNav } from '../hooks/useOwnerNav';
import { ownerScreenList } from '../data/ownerScreens';
import type { OwnerNav, OwnerScreen } from '../types/owner';
const screens: Record<OwnerScreen, React.ComponentType<{
  nav: OwnerNav;
}>> = {
  splash: OwnerSplash,
  onboarding: OwnerOnboarding,
  register: OwnerRegister,
  otp: OwnerOtp,
  login: OwnerLogin,
  profileForm: OwnerProfileForm,
  home: OwnerHome,
  addVehicle: OwnerAddVehicle,
  vehicleForm: OwnerVehicleForm,
  documents: OwnerDocuments,
  brta: OwnerBrtaCheck,
  selfVerify: OwnerSelfVerify,
  submitted: OwnerSubmitted,
  tracking: OwnerTracking,
  vehicles: OwnerVehicles,
  vehicleDetail: OwnerVehicleDetail,
  drivers: OwnerDrivers,
  addDriver: OwnerAddDriver,
  docTracker: OwnerDocTracker,
  qr: OwnerQrSticker,
  notifications: OwnerNotifications,
  expiry: OwnerExpiry,
  correction: OwnerCorrection,
  ownership: OwnerOwnership,
  history: OwnerHistory,
  notices: OwnerNotices,
  support: OwnerSupport,
  account: OwnerAccount
};
const journey = ['মালিক নিবন্ধন', 'গাড়ি যুক্তকরণ', 'ডকুমেন্ট আপলোড', 'স্ব-যাচাই', 'ডিএমপি অনুমোদন', 'QR স্টিকার ইস্যু', 'ট্রাফিক পুলিশ যাচাই', 'তথ্য ও ডকুমেন্ট ট্র্যাকিং'];
export function OwnerApp() {
  const nav = useOwnerNav('splash');
  const navigate = useNavigate();
  const Screen = screens[nav.screen];
  const info = ownerScreenList.find(s => s.key === nav.screen);
  const flows: {
    title: string;
    path: string;
    icon: typeof Bell;
    cls: string;
    run: () => void;
  }[] = [{
    title: 'ফ্লো ১: নতুন মালিক নিবন্ধন',
    path: 'স্বাগতম → নিবন্ধন → OTP → প্রোফাইল → ড্যাশবোর্ড',
    icon: UserPlus,
    cls: 'border-navy-200 bg-navy-50 text-navy-800',
    run: () => nav.reset('onboarding')
  }, {
    title: 'ফ্লো ২: নতুন সিএনজি নিবন্ধন',
    path: 'গাড়ি যুক্ত → সিএনজি → তথ্য → ডকুমেন্ট → BRTA → স্ব-যাচাই → জমা → ট্র্যাক',
    icon: CarTaxiFront,
    cls: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    run: () => nav.reset('addVehicle')
  }, {
    title: 'ফ্লো ৩: মোটরসাইকেল নিবন্ধন',
    path: 'গাড়ি যুক্ত → মোটরসাইকেল → তথ্য → প্রযোজ্য ডকুমেন্ট → স্ব-যাচাই → জমা',
    icon: Bike,
    cls: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    run: () => nav.reset('addVehicle')
  }, {
    title: 'ফ্লো ৪: ডিএমপি অনুমোদন (ওয়েব)',
    path: 'মালিকের আবেদন → পর্যালোচনা → BRTA তুলনা → অনুমোদন → QR → নোটিফিকেশন',
    icon: Monitor,
    cls: 'border-blue-200 bg-blue-50 text-blue-800',
    run: () => navigate('/admin/owner/applications')
  }, {
    title: 'ফ্লো ৫: ডকুমেন্ট ট্র্যাকিং',
    path: 'আমার গাড়ি → বিস্তারিত → ডকুমেন্ট → মেয়াদ সতর্কতা → হালনাগাদ আবেদন',
    icon: FileSearch,
    cls: 'border-amber-200 bg-amber-50 text-amber-800',
    run: () => {
      nav.setVehicleId('c1');
      nav.setDetailTab('docs');
      nav.reset('vehicles');
    }
  }, {
    title: 'ফ্লো ৬: সিএনজিতে চালক যুক্ত',
    path: 'আমার গাড়ি → সিএনজি → চালক → সংযুক্তির আবেদন → লাইসেন্স → ডকুমেন্ট → জমা',
    icon: Users,
    cls: 'border-amber-200 bg-amber-50 text-amber-800',
    run: () => {
      nav.setVehicleId('c1');
      nav.setDetailTab('drivers');
      nav.reset('vehicles');
    }
  }, {
    title: 'ফ্লো ৭: নোটিফিকেশন',
    path: 'নোটিফিকেশন → QR স্টিকার প্রস্তুত → স্টিকারের বিস্তারিত → সংগ্রহের তথ্য',
    icon: Bell,
    cls: 'border-red-200 bg-red-50 text-red-800',
    run: () => nav.reset('notifications')
  }, {
    title: 'ফ্লো ৮: গাড়ির তথ্য সংশোধন',
    path: 'বিস্তারিত → সংশোধনের আবেদন → নতুন তথ্য → প্রমাণ → জমা → ট্র্যাক',
    icon: FilePen,
    cls: 'border-slate-200 bg-slate-50 text-slate-800',
    run: () => {
      nav.setVehicleId('c1');
      nav.setDetailTab('info');
      nav.reset('vehicleDetail');
    }
  }];
  return <div className="min-h-[calc(100vh-48px)] w-full bg-slate-100" data-id="element-3687" data-mp="tnw003r1q517">
      <div className="mx-auto flex max-w-[1320px] flex-col items-center gap-6 px-4 py-6 lg:flex-row lg:items-start lg:justify-center" data-id="element-3688" data-mp="yotmgsj39l8i">
        <aside className="w-full max-w-[320px] space-y-4 lg:sticky lg:top-16" data-id="element-3689" data-mp="30cxvojiyogc9">
          <div className="rounded-lg border border-border bg-white p-4 shadow-sm" data-id="element-3690" data-mp="2vfxpm8m43dl5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-bdgreen-700" data-id="element-3691" data-mp="2qmutpttv3s1s">C. গাড়ির মালিকের মোবাইল অ্যাপ</p>
            <h1 className="mt-1 font-heading text-lg font-bold text-navy-900" data-id="element-3692" data-mp="2mm2k2zh2vl4z">ডিএমপি গাড়ির মালিক সেবা</h1>
            <p className="mt-1 text-xs text-slate-500" data-id="element-3693" data-mp="2hoj3vu0tkyae">যানবাহন নিবন্ধন, তথ্য যাচাই ও ডিজিটাল সেবা • নিচের ফ্লো থেকে শুরু করুন।</p>
            <div className="mt-3 grid max-h-[330px] gap-1.5 overflow-y-auto pr-1" data-id="element-3694" data-mp="27p1zdfxb0qg0">
              {flows.map(f => <button key={f.title} onClick={f.run} className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-left text-[12px] font-semibold ${f.cls} hover:brightness-95`} data-id="element-3695" data-mp="23o8eywj2ltrv">
                  <f.icon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-3696" data-mp="1wd94xjhwjhk" />
                  <span data-id="element-3697" data-mp="1tzbqi1cev6u">{f.title}<span className="block text-[10.5px] font-normal opacity-80" data-id="element-3698" data-mp="1u91c4ih70c0">{f.path}</span></span>
                </button>)}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-white p-2 shadow-sm" data-id="element-3699" data-mp="1oqywplxlhgf5">
            <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold text-slate-500" data-id="element-3700" data-mp="1tktf8m1grpcn">সকল স্ক্রিন ({ownerScreenList.length})</p>
            <ul className="no-scrollbar max-h-[300px] overflow-y-auto" data-id="element-3701" data-mp="1xn7smqmd5wwj">
              {ownerScreenList.map(s => <li key={s.key} data-id="element-3702" data-mp="15txqxyw7vca5">
                  <button onClick={() => nav.reset(s.key)} aria-current={nav.screen === s.key ? 'true' : undefined} className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] ${nav.screen === s.key ? 'bg-navy-800 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-id="element-3703" data-mp="15uz01xto9op8">
                    <span className={`w-8 font-mono text-[11px] ${nav.screen === s.key ? 'text-navy-200' : 'text-slate-400'}`} data-id="element-3704" data-mp="2q99exayaff3h">{s.no}</span>
                    {s.label}
                  </button>
                </li>)}
            </ul>
          </div>
        </aside>

        <PhoneFrame toast={nav.toast} data-id="element-3705" data-mp="2apismjgxk3dl">
          <Screen nav={nav} data-id="element-3706" data-mp="2aqkgjoj1okcq" />
        </PhoneFrame>

        <aside className="w-full max-w-[320px] space-y-4 lg:sticky lg:top-16" data-id="element-3707" data-mp="2ardga5jl69ld">
          <div className="rounded-lg border border-border bg-white p-4 shadow-sm" data-id="element-3708" data-mp="25xj03c36wcr1">
            <p className="text-[11px] font-semibold text-slate-500" data-id="element-3709" data-mp="2kmf7d6xf9lql">বর্তমান স্ক্রিন</p>
            <p className="mt-1 flex items-center gap-2 font-heading text-base font-bold text-navy-900" data-id="element-3710" data-mp="2fnkdn9xx2crp">
              <span className="rounded bg-navy-800 px-1.5 py-0.5 font-mono text-xs text-white" data-id="element-3711" data-mp="2tn3jtx763e1d">{info?.no}</span>
              {info?.label}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-3712" data-mp="zadkv11xzen4">{info?.desc}</p>
          </div>
          <div className="rounded-lg border border-border bg-white p-4 shadow-sm" data-id="element-3713" data-mp="v91ugtb8updv">
            <p className="mb-2 text-xs font-semibold text-navy-900" data-id="element-3714" data-mp="vb4vs9huk4xw">সম্পূর্ণ ডিজিটাল সেবা যাত্রা</p>
            <ol className="space-y-0.5" data-id="element-3715" data-mp="l77qgbrou9y3">
              {journey.map((j, i) => <li key={j} className="flex flex-col items-start" data-id="element-3716" data-mp="p8jfog6ngt5r" data-mpi={i}>
                  <span className="rounded-md bg-slate-50 px-2 py-1 text-[12px] font-medium text-slate-700" data-id="element-3717" data-mp="be8ycjwzg19q">{j}</span>
                  {i < journey.length - 1 && <ArrowDown className="ml-3 h-3 w-3 text-slate-300" data-id="element-3718" data-mp="gbsczkxleg27" />}
                </li>)}
            </ol>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs leading-relaxed text-blue-900" data-id="element-3719" data-mp="6g7ehwrqfxx7">
            <p className="font-semibold" data-id="element-3720" data-mp="3omvitkeykh1t">তিন অ্যাপে একই তথ্য</p>
            <p className="mt-1" data-id="element-3721" data-mp="3tkdn2xi1683h">মালিক অ্যাপ, ওয়েব অ্যাডমিন ও ট্রাফিক পুলিশ অ্যাপ একই গাড়ি, মালিক, চালক ও QR রেকর্ড ব্যবহার করে। অনুমোদিত রেকর্ডে পরিবর্তন কেবল ডিএমপি অনুমোদনের পর কার্যকর হয়। সকল তথ্য নমুনা।</p>
            <div className="mt-2 flex gap-2" data-id="element-3722" data-mp="1rtjyxltz8ijv">
              <button onClick={() => navigate('/admin/owner')} className="rounded border border-blue-300 bg-white px-2 py-1 font-semibold" data-id="element-3723" data-mp="1rr7jpgflsmai">মালিক সেবা ড্যাশবোর্ড</button>
              <button onClick={() => navigate('/mobile')} className="rounded border border-blue-300 bg-white px-2 py-1 font-semibold" data-id="element-3724" data-mp="3mm2jg79dfsbb">ট্রাফিক অ্যাপ</button>
            </div>
          </div>
        </aside>
      </div>
    </div>;
}